/**
 * Schrittmacher für Live-Scores der Pro-Spiele (2026-10-18).
 *
 * PURE Logik, 1:1-Port in der App: dartcloud-frontend/lib/core/utils/live_score_pacer.dart
 * — Änderungen hier IMMER dort nachziehen, die Tests teilen sich die Fixtures.
 *
 * Problem (Marcel): Der Backend-Poller schickt alle 4 s (Socket) bzw. die
 * Detailseite holt alle 15 s den KOMPLETTEN Stand eines Spiels — Restpunkte,
 * Legs und die gesamte Wurfhistorie (`throwByThrow.BLOCKS`). Kommen in einem
 * Update zwei oder mehr Aufnahmen auf einmal an, „springen“ die Restpunkte
 * (z. B. 361 → 81 statt 361 → 221 → 81).
 *
 * Lösung: Aus dem angezeigten Stand und dem neuen Stand wird eine Folge von
 * Zwischenständen gebaut, die der Reihe nach (STEP_DELAY_MS) angezeigt werden:
 * - ein Schritt je neuer Aufnahme (SCORE-Eintrag in der Wurfhistorie)
 * - ein eigener Schritt für das Leg-Ende (Checkout + Legs-Stand), weil der
 *   Checkout-Wurf in der Historie nie auftaucht (MAIN_SCORE springt stattdessen)
 * Jeder Zwischenstand ist ein vollständiges liveData-Objekt (Blocks auf den
 * sichtbaren Stand gekürzt, Restpunkte daraus neu berechnet) — Listen-Karte,
 * Übersicht und Scoreboard rendern ihn wie ein echtes Update.
 *
 * Sofort (ohne Schritte) wird angezeigt bei: erstem Stand, fehlenden Blocks,
 * rückwärts laufenden Daten (FlashLive baut Blocks um), mehr als einem
 * Leg-Ende auf einmal oder mehr als MAX_PACED_STEPS Schritten (Reconnect).
 */

import { parseThrowByThrow } from '../services/throwByThrowParser';

export const STEP_DELAY_MS = 1500;
export const MAX_PACED_STEPS = 6;
/** Startwert eines Legs (Pro-Darts: 501) */
export const LEG_START_SCORE = 501;

export interface PacePosition {
  /** Anzahl SCORE-Einträge über alle Blocks */
  visits: number;
  /** Summe MAIN_SCORE des letzten Blocks (= beendete Legs laut FlashLive) */
  legs: number;
}

type AnyRecord = Record<string, any>;

const toInt = (v: any): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : null;
  const n = parseInt(String(v ?? ''), 10);
  return Number.isNaN(n) ? null : n;
};

export const getBlocks = (liveData: AnyRecord | null | undefined): AnyRecord[] => {
  const blocks = liveData?.throwByThrow?.BLOCKS;
  return Array.isArray(blocks) ? blocks : [];
};

const historyOf = (block: AnyRecord): AnyRecord[] =>
  Array.isArray(block?.SCORE_HISTORY) ? block.SCORE_HISTORY : [];

const isScoreEntry = (entry: AnyRecord): boolean => entry?.TYPE === 'SCORE';

/** Restpunkte beider Spieler aus einem SCORE-Eintrag (null, wenn unvollständig) */
export const entryRemaining = (
  entry: AnyRecord | null | undefined,
): { home: number; away: number; changed: 1 | 2 | null } | null => {
  const items = Array.isArray(entry?.ITEMS) ? entry!.ITEMS : [];
  let home: number | null = null;
  let away: number | null = null;
  let changed: 1 | 2 | null = null;
  for (const item of items) {
    const order = toInt(item?.TEAM_ORDER);
    const value = toInt(item?.VALUE);
    if (value === null) continue;
    if (order === 1) home = value;
    if (order === 2) away = value;
    if (toInt(item?.IS_CHANGED) === 1 && (order === 1 || order === 2)) changed = order;
  }
  if (home === null || away === null) return null;
  return { home, away, changed };
};

export const countVisits = (blocks: AnyRecord[]): number => {
  let count = 0;
  for (const block of blocks) {
    for (const entry of historyOf(block)) if (isScoreEntry(entry)) count += 1;
  }
  return count;
};

const mainScoreOf = (block: AnyRecord): { home: number; away: number; changed: 1 | 2 | null } => {
  let home = 0;
  let away = 0;
  let changed: 1 | 2 | null = null;
  const main = Array.isArray(block?.MAIN_SCORE) ? block.MAIN_SCORE : [];
  for (const ms of main) {
    const order = toInt(ms?.TEAM_ORDER);
    const value = toInt(ms?.VALUE) ?? 0;
    if (order === 1) home = value;
    if (order === 2) away = value;
    if (toInt(ms?.IS_CHANGED) === 1 && (order === 1 || order === 2)) changed = order;
  }
  return { home, away, changed };
};

export const lastBlockLegSum = (blocks: AnyRecord[]): number => {
  if (blocks.length === 0) return 0;
  const main = mainScoreOf(blocks[blocks.length - 1]);
  return main.home + main.away;
};

export const positionOf = (liveData: AnyRecord | null | undefined): PacePosition | null => {
  const blocks = getBlocks(liveData);
  if (blocks.length === 0) return null;
  return { visits: countVisits(blocks), legs: lastBlockLegSum(blocks) };
};

/** Alle SCORE-Einträge in Reihenfolge (blockübergreifend), mit Block-Index */
const flattenScores = (blocks: AnyRecord[]): { blockIndex: number; entry: AnyRecord }[] => {
  const out: { blockIndex: number; entry: AnyRecord }[] = [];
  blocks.forEach((block, blockIndex) => {
    for (const entry of historyOf(block)) if (isScoreEntry(entry)) out.push({ blockIndex, entry });
  });
  return out;
};

/**
 * Beginnt mit dem Eintrag an Position `index` (0-basiert, blockübergreifend)
 * ein neues Leg? Ja bei erstem Eintrag eines Blocks oder wenn der Rest des
 * werfenden Spielers STEIGT (X01-Rest fällt sonst immer).
 */
export const isLegStartEntry = (blocks: AnyRecord[], index: number): boolean => {
  const flat = flattenScores(blocks);
  const current = flat[index];
  if (!current) return false;
  if (index === 0 || flat[index - 1].blockIndex !== current.blockIndex) return true;
  const now = entryRemaining(current.entry);
  const before = entryRemaining(flat[index - 1].entry);
  if (!now || !before || !now.changed) return false;
  const nowValue = now.changed === 1 ? now.home : now.away;
  const beforeValue = now.changed === 1 ? before.home : before.away;
  return nowValue > beforeValue;
};

/** Blocks auf die ersten `visits` SCORE-Einträge kürzen (TEXT_SPEC-Marker der letzten Aufnahme bleiben) */
export const truncateBlocks = (blocks: AnyRecord[], visits: number): AnyRecord[] => {
  const out: AnyRecord[] = [];
  let remaining = visits;
  for (const block of blocks) {
    if (remaining <= 0) break;
    const history = historyOf(block);
    const kept: AnyRecord[] = [];
    let cutHere = false;
    for (const entry of history) {
      if (isScoreEntry(entry)) {
        if (remaining <= 0) {
          cutHere = true;
          break;
        }
        kept.push(entry);
        remaining -= 1;
      } else {
        // Marker (z. B. "180") gehört zur direkt davor gelisteten Aufnahme
        kept.push(entry);
      }
    }
    const complete = !cutHere && kept.length === history.length;
    out.push(complete ? block : { ...block, SCORE_HISTORY: kept });
  }
  return out;
};

const mainScoreMinusOne = (block: AnyRecord): AnyRecord[] | null => {
  const main = mainScoreOf(block);
  if (!main.changed) return null;
  const home = main.changed === 1 ? main.home - 1 : main.home;
  const away = main.changed === 2 ? main.away - 1 : main.away;
  if (home < 0 || away < 0) return null;
  return [
    { TEAM_ORDER: 1, VALUE: String(home) },
    { TEAM_ORDER: 2, VALUE: String(away) },
  ];
};

const scoreMinusOne = (score: any, minus: boolean): any => {
  if (!minus) return score;
  const current = toInt(score?.current);
  if (current === null || current <= 0) return score;
  return { ...(score ?? {}), current: current - 1 };
};

/**
 * Zwischenstand: die ersten `visits` Aufnahmen sichtbar. `pendingLegEnd` =
 * das Leg-Ende des letzten Blocks ist noch NICHT dran (MAIN_SCORE und Legs-
 * Stand werden um das noch offene Leg zurückgenommen).
 * Gibt null zurück, wenn der Stand nicht sauber ableitbar ist (→ Aufrufer springt).
 */
export const buildPacedState = (
  incoming: AnyRecord,
  visits: number,
  pendingLegEnd: boolean,
): AnyRecord | null => {
  const blocks = truncateBlocks(getBlocks(incoming), visits);
  if (blocks.length === 0) return null;

  let lastIndex = blocks.length - 1;
  let winner: 1 | 2 | null = null;
  if (pendingLegEnd) {
    const reduced = mainScoreMinusOne(blocks[lastIndex]);
    if (!reduced) return null;
    winner = mainScoreOf(blocks[lastIndex]).changed;
    blocks[lastIndex] = { ...blocks[lastIndex], MAIN_SCORE: reduced };
  }

  const lastBlock = blocks[lastIndex];
  const history = historyOf(lastBlock);
  let lastScore: AnyRecord | null = null;
  let lastThrow: string | null = null;
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i];
    if (!lastScore && isScoreEntry(entry)) lastScore = entry;
    if (lastThrow === null && !isScoreEntry(entry) && entry?.VALUE != null) lastThrow = String(entry.VALUE);
    if (lastScore && lastThrow !== null) break;
  }
  const rem = entryRemaining(lastScore);
  const legOver = !rem || rem.home === 0 || rem.away === 0;
  const homeRemaining = legOver ? null : rem!.home;
  const awayRemaining = legOver ? null : rem!.away;

  const thrower = typeof incoming.currentThrower === 'string' ? incoming.currentThrower : null;
  const throwerScore = thrower === 'home' ? homeRemaining : thrower === 'away' ? awayRemaining : null;

  const onSets = incoming.playingOnSets === true || incoming.playingOnSets === 1 || incoming.playingOnSets === '1';
  const state: AnyRecord = {
    ...incoming,
    throwByThrow: { ...(incoming.throwByThrow ?? {}), BLOCKS: blocks },
    homeRemainingScore: homeRemaining,
    awayRemainingScore: awayRemaining,
    currentThrowerScore: throwerScore,
    lastThrow: legOver ? null : lastThrow,
  };
  if (pendingLegEnd && winner) {
    if (onSets && incoming.legScore && typeof incoming.legScore === 'object') {
      const key = winner === 1 ? 'home' : 'away';
      const legs = toInt(incoming.legScore[key]);
      state.legScore = { ...incoming.legScore, [key]: legs !== null && legs > 0 ? legs - 1 : legs };
    } else if (!onSets) {
      state.homeScore = scoreMinusOne(incoming.homeScore, winner === 1);
      state.awayScore = scoreMinusOne(incoming.awayScore, winner === 2);
    }
  }
  return state;
};

/**
 * Folge der anzuzeigenden Stände vom angezeigten zum neuen Stand.
 * Das letzte Element ist IMMER das unveränderte `incoming`.
 */
export const planPacedStates = (
  displayed: AnyRecord | null | undefined,
  incoming: AnyRecord,
  maxSteps: number = MAX_PACED_STEPS,
): AnyRecord[] => {
  const jump = [incoming];
  const from = positionOf(displayed);
  const to = positionOf(incoming);
  if (!from || !to) return jump;
  if (to.visits < from.visits || to.legs < from.legs) return jump;
  if (to.legs - from.legs > 1) return jump;
  const totalSteps = to.visits - from.visits + (to.legs - from.legs);
  if (totalSteps <= 0) return jump;
  if (totalSteps > maxSteps) return jump;

  const blocks = getBlocks(incoming);
  const states: AnyRecord[] = [];
  let visits = from.visits;
  let legs = from.legs;
  while (visits < to.visits || legs < to.legs) {
    const legEndNext = legs < to.legs && (visits === to.visits || isLegStartEntry(blocks, visits));
    if (legEndNext) {
      legs += 1;
    } else {
      visits += 1;
    }
    if (visits === to.visits && legs === to.legs) break;
    const state = buildPacedState(incoming, visits, legs < to.legs);
    if (!state) return jump;
    states.push(state);
  }
  states.push(incoming);
  return states;
};

/**
 * Leg zu Ende, nächstes Leg noch ohne Wurf (Marcel 2026-10-18): FlashLive
 * schreibt den Checkout nie in die Historie, der Poller ließ deshalb die
 * letzten Reste (46/81) stehen, bis der erste Wurf des neuen Legs kam. Der
 * geteilte Parser liefert in dem Fall ein leeres laufendes Leg → Restpunkte
 * sofort 501/501. Ohne Blocks oder bei beendetem Match bleibt alles wie es ist.
 * Gibt dasselbe Objekt zurück, wenn nichts zu ändern ist.
 */
export const applyLegResetRemaining = (state: AnyRecord): AnyRecord => {
  const blocks = getBlocks(state);
  if (blocks.length === 0) return state;
  if (String(state.stageType ?? '').toUpperCase() === 'FINISHED') return state;
  let current: { visits: unknown[] } | null;
  try {
    current = parseThrowByThrow(blocks, false).currentLeg;
  } catch {
    return state;
  }
  if (!current || current.visits.length > 0) return state;
  if (state.homeRemainingScore === LEG_START_SCORE && state.awayRemainingScore === LEG_START_SCORE) return state;
  const thrower = typeof state.currentThrower === 'string' ? state.currentThrower : null;
  return {
    ...state,
    homeRemainingScore: LEG_START_SCORE,
    awayRemainingScore: LEG_START_SCORE,
    currentThrowerScore: thrower === 'home' || thrower === 'away' ? LEG_START_SCORE : state.currentThrowerScore ?? null,
    lastThrow: null,
  };
};

/**
 * Wer wirft gerade? (Marcel 2026-10-18, Punkt in Liste/Ticker) Der Backend-Wert
 * `currentThrower` ist der Anwerfer des Legs (SERVICE_PARTICIPANT_TYPE) und
 * bleibt das ganze Leg gleich – live geprüft. Richtig ist: der Spieler, der
 * NICHT zuletzt geworfen hat; bei leerem neuen Leg der Anwerfer dieses Legs.
 * Ohne Blocks bleibt der Backend-Wert. Gibt dasselbe Objekt zurück, wenn nichts
 * zu ändern ist.
 */
export const applyThrowerFromHistory = (state: AnyRecord): AnyRecord => {
  const blocks = getBlocks(state);
  if (blocks.length === 0) return state;
  if (String(state.stageType ?? '').toUpperCase() === 'FINISHED') return state;
  let current: { visits: { team: 1 | 2 }[]; starter: 1 | 2 | null } | null;
  try {
    current = parseThrowByThrow(blocks, false).currentLeg;
  } catch {
    return state;
  }
  if (!current) return state;
  let team: 1 | 2 | null = null;
  if (current.visits.length > 0) {
    team = current.visits[current.visits.length - 1].team === 1 ? 2 : 1;
  } else {
    team = current.starter;
  }
  if (team === null) return state;
  const thrower = team === 1 ? 'home' : 'away';
  const score = team === 1 ? state.homeRemainingScore : state.awayRemainingScore;
  if (state.currentThrower === thrower && (state.currentThrowerScore ?? null) === (score ?? null)) return state;
  return { ...state, currentThrower: thrower, currentThrowerScore: score ?? null };
};

/** Alle Korrekturen für einen anzuzeigenden Stand (Reihenfolge: erst 501/501, dann Werfer) */
export const finalizeDisplayedState = (state: AnyRecord): AnyRecord =>
  applyThrowerFromHistory(applyLegResetRemaining(state));

export type PacerDisplay<T> = (key: string, liveData: T) => void;

export interface LiveScorePacerOptions {
  stepDelayMs?: number;
  maxSteps?: number;
  setTimer?: (fn: () => void, ms: number) => any;
  clearTimer?: (handle: any) => void;
}

/**
 * Hält je Spiel den zuletzt ANGEZEIGTEN Stand und spielt neue Stände
 * schrittweise aus. `push` darf beliebig oft kommen — ein neuer Stand ersetzt
 * die noch offene Schrittfolge (geplant ab dem gerade angezeigten Stand), es
 * entsteht nie ein Rückstau.
 */
export class LiveScorePacer<T extends AnyRecord = AnyRecord> {
  private displayed = new Map<string, T>();
  private queues = new Map<string, T[]>();
  private timers = new Map<string, any>();
  private readonly stepDelayMs: number;
  private readonly maxSteps: number;
  private readonly setTimer: (fn: () => void, ms: number) => any;
  private readonly clearTimer: (handle: any) => void;

  constructor(private readonly onDisplay: PacerDisplay<T>, options: LiveScorePacerOptions = {}) {
    this.stepDelayMs = options.stepDelayMs ?? STEP_DELAY_MS;
    this.maxSteps = options.maxSteps ?? MAX_PACED_STEPS;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? ((h) => clearTimeout(h));
  }

  /** Zuletzt angezeigter Stand (für Listen-Neuladungen, die den Socket-Stand nicht überschreiben sollen) */
  displayedFor(key: string): T | null {
    return this.displayed.get(key) ?? null;
  }

  push(key: string, incoming: T): void {
    const base = this.displayed.get(key) ?? null;
    // FlashLive liefert im Leg-Übergang 30–90 s leere BLOCKS; die Ansichten
    // behalten dann den alten Stand — der Schrittmacher merkt sich die Blocks
    // ebenfalls, sonst würde das nächste echte Update wieder springen.
    if (!positionOf(incoming) && base && positionOf(base)) {
      this.queues.delete(key);
      const handle = this.timers.get(key);
      if (handle !== undefined) this.clearTimer(handle);
      this.timers.delete(key);
      this.onDisplay(key, incoming);
      this.displayed.set(key, { ...incoming, throwByThrow: base.throwByThrow } as T);
      return;
    }
    const plan = planPacedStates(base, incoming, this.maxSteps) as T[];
    if (this.timers.has(key)) {
      // Schrittfolge läuft: nur die Warteschlange ersetzen, der Takt bleibt
      this.queues.set(key, plan);
      return;
    }
    this.emit(key, plan[0]);
    if (plan.length > 1) {
      this.queues.set(key, plan.slice(1));
      this.schedule(key);
    }
  }

  /** Offene Schritte verwerfen (Spielende, Ansicht verlassen) */
  reset(key?: string): void {
    const keys = key === undefined ? Array.from(this.timers.keys()) : [key];
    for (const k of keys) {
      const handle = this.timers.get(k);
      if (handle !== undefined) this.clearTimer(handle);
      this.timers.delete(k);
      this.queues.delete(k);
    }
    if (key === undefined) this.displayed.clear();
    else this.displayed.delete(key);
  }

  dispose(): void {
    this.reset();
  }

  private emit(key: string, state: T): void {
    const shown = finalizeDisplayedState(state) as T;
    this.displayed.set(key, shown);
    this.onDisplay(key, shown);
  }

  private schedule(key: string): void {
    const handle = this.setTimer(() => {
      this.timers.delete(key);
      const queue = this.queues.get(key) ?? [];
      const next = queue.shift();
      if (next) this.emit(key, next);
      if (queue.length > 0) {
        this.queues.set(key, queue);
        this.schedule(key);
      } else {
        this.queues.delete(key);
      }
    }, this.stepDelayMs);
    this.timers.set(key, handle);
  }
}
